import React, { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '../../../../components/Modals/Modal';
import { Container, Form, Row } from '../../../../components/commons';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import {
  useSyncApiIntegration,
  useSyncTceCe,
  useMunicipiosTce,
} from '../../../../services/apiIntegrations.service';
import useToast from '../../../../hooks/useToast';
import { ApiIntegration } from '../../../../interfaces/ApiIntegrations';

export interface SyncModalHandle {
  openSyncModal: (integrationOrId: ApiIntegration | string) => void;
}

type ModalRef = React.ElementRef<typeof Modal>;

const defaultSchema = yup.object().shape({
  startDate: yup
    .string()
    .required('Data Inicial obrigatória')
    .matches(/^\d{6}$/, 'Formato AAAAMM esperado (ex: 202401)'),
  endDate: yup
    .string()
    .required('Data Final obrigatória')
    .matches(/^\d{6}$/, 'Formato AAAAMM esperado (ex: 202412)'),
});

const SyncModal: React.ForwardRefRenderFunction<SyncModalHandle, {}> = (props, ref) => {
  const modalRef = useRef<ModalRef>(null);
  const toast = useToast();
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [currentIntegration, setCurrentIntegration] = useState<ApiIntegration | null>(null);
  const [activeTab, setActiveTab] = useState<'standard' | 'tce'>('tce');

  // Estado para o formulário TCE-CE
  const [codigoMunicipio, setCodigoMunicipio] = useState('072'); // Default: Icapuí
  const [exercicio, setExercicio] = useState(new Date().getFullYear());
  const [mesInicio, setMesInicio] = useState(1);
  const [mesFim, setMesFim] = useState(12);
  const [syncDespesas, setSyncDespesas] = useState(true);
  const [syncLicitacoes, setSyncLicitacoes] = useState(true);
  const [anosEmpenhosTexto, setAnosEmpenhosTexto] = useState('');

  const { data: municipios, isLoading: loadingMunicipios } = useMunicipiosTce();
  const syncMutation = useSyncApiIntegration();
  const syncTceMutation = useSyncTceCe();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(defaultSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
    },
  });

  // Função auxiliar para identificar o código do município a partir da integração
  const findMunicipioCode = (integr: ApiIntegration | null, munList: any[]) => {
    if (!integr) return null;
    if (integr.codigoMunicipio) return integr.codigoMunicipio;
    const name = (integr.name || '').toUpperCase();
    if (!munList || munList.length === 0) return null;
    const found = munList.find((m: any) =>
      name.includes(m.nome.toUpperCase()) || name.includes(m.codigo)
    );
    return found ? found.codigo : null;
  };

  useEffect(() => {
    if (currentIntegration && municipios && municipios.length > 0) {
      const detected = findMunicipioCode(currentIntegration, municipios);
      if (detected) {
        setCodigoMunicipio(detected);
      }
    }
  }, [currentIntegration, municipios]);

  React.useImperativeHandle(ref, () => ({
    openSyncModal: (integrationOrId: ApiIntegration | string) => {
      let id = '';
      let integrObj: ApiIntegration | null = null;
      if (typeof integrationOrId === 'string') {
        id = integrationOrId;
      } else if (integrationOrId && typeof integrationOrId === 'object') {
        id = integrationOrId._id as string;
        integrObj = integrationOrId;
      }

      setCurrentId(id);
      setCurrentIntegration(integrObj);

      if (integrObj && municipios) {
        const detected = findMunicipioCode(integrObj, municipios);
        if (detected) setCodigoMunicipio(detected);
      }

      reset({ startDate: '', endDate: '' });
      modalRef.current?.openModal();
    },
  }));

  const onSubmitStandard = async (data: { startDate: string; endDate: string }) => {
    if (!currentId) return;

    try {
      await syncMutation.mutateAsync({
        id: currentId,
        startDate: data.startDate,
        endDate: data.endDate,
      });

      toast.successToast('Sincronização iniciada com sucesso!');
      modalRef.current?.closeModal();
    } catch (error) {
      toast.errorToast('Erro ao iniciar sincronização.');
    }
  };

  const handleSyncTce = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentId) return;

    const tipos: string[] = [];
    if (syncDespesas) tipos.push('DESPESAS');
    if (syncLicitacoes) tipos.push('LICITACOES');

    if (tipos.length === 0) {
      toast.errorToast('Selecione ao menos um tipo de dado para sincronizar.');
      return;
    }

    // Processar anos adicionais de empenhos (ex: "2023, 2022")
    const anosEmpenhosArr: number[] = [];
    if (anosEmpenhosTexto.trim()) {
      anosEmpenhosTexto
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 2000 && n <= 2100)
        .forEach((n) => {
          if (!anosEmpenhosArr.includes(n)) anosEmpenhosArr.push(n);
        });
    }

    try {
      const response = await syncTceMutation.mutateAsync({
        id: currentId,
        codigoMunicipio,
        exercicio: Number(exercicio),
        mesInicio: Number(mesInicio),
        mesFim: Number(mesFim),
        tipos,
        anosEmpenhos: anosEmpenhosArr.length > 0 ? anosEmpenhosArr : undefined,
      } as any);

      const stats = response?.stats;
      const total = stats?.totalInseridos || 0;
      const anosEmp = stats?.anosEmpenhosSincronizados?.length
        ? ` | Empenhos dos anos: [${stats.anosEmpenhosSincronizados.join(', ')}]`
        : '';

      toast.successToast(
        `Sincronização concluída! ${total} registros inseridos${anosEmp} (NP: ${stats?.notasPagamentos || 0}, NE: ${stats?.notasEmpenhos || 0}, LIC: ${stats?.licitacoes || 0}).`
      );
      modalRef.current?.closeModal();
    } catch (error: any) {
      toast.errorToast(
        error.response?.data?.message || 'Erro ao sincronizar com a API do TCE-CE.'
      );
    }
  };

  return (
    <Modal ref={modalRef} title='Sincronizar Dados de Integração'>
      <Container>
        {/* Abas de Navegação */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 20 }}>
          <button
            type='button'
            onClick={() => setActiveTab('tce')}
            style={{
              padding: '10px 18px',
              border: 'none',
              borderBottom: activeTab === 'tce' ? '3px solid #2563eb' : 'none',
              background: 'transparent',
              color: activeTab === 'tce' ? '#2563eb' : '#64748b',
              fontWeight: activeTab === 'tce' ? 700 : 500,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            🏛️ API TCE-CE (SIM - Dados Abertos)
          </button>
          <button
            type='button'
            onClick={() => setActiveTab('standard')}
            style={{
              padding: '10px 18px',
              border: 'none',
              borderBottom: activeTab === 'standard' ? '3px solid #2563eb' : 'none',
              background: 'transparent',
              color: activeTab === 'standard' ? '#2563eb' : '#64748b',
              fontWeight: activeTab === 'standard' ? 700 : 500,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            ⚙️ Sincronização Padrão (URL)
          </button>
        </div>

        {/* Tab 1: TCE-CE Ingestion */}
        {activeTab === 'tce' && (
          <form onSubmit={handleSyncTce}>
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
              Ingestão direta a partir da <strong>API de Dados Abertos do TCE-CE</strong> (Tribunal
              de Contas do Estado do Ceará), alimentando notas de pagamentos, empenhos, despesas
              extras e processos licitatórios.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                Município Vinculado {currentIntegration?.name ? `(Integração: ${currentIntegration.name})` : ''}
              </label>
              <select
                value={codigoMunicipio}
                onChange={(e) => setCodigoMunicipio(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: '#0f172a',
                  backgroundColor: '#f8fafc',
                }}
              >
                {loadingMunicipios ? (
                  <option value='072'>Carregando municípios...</option>
                ) : municipios && municipios.length > 0 ? (
                  municipios.map((m: any) => (
                    <option key={m.codigo} value={m.codigo}>
                      {m.codigo} - {m.nome}
                    </option>
                  ))
                ) : (
                  <>
                    <option value='072'>072 - ICAPUI</option>
                    <option value='005'>005 - ACOPIARA</option>
                    <option value='104'>104 - MAURITI</option>
                    <option value='169'>169 - TIANGUA</option>
                  </>
                )}
              </select>
            </div>

            <Row justify='space-between' gap={12}>
              <div style={{ flex: 1, marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Exercício (Ano)
                </label>
                <input
                  type='number'
                  min={2000}
                  max={2100}
                  value={exercicio}
                  onChange={(e) => setExercicio(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: 13,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ flex: 1, marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Mês Inicial
                </label>
                <input
                  type='number'
                  min={1}
                  max={12}
                  value={mesInicio}
                  onChange={(e) => setMesInicio(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: 13,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ flex: 1, marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Mês Final
                </label>
                <input
                  type='number'
                  min={1}
                  max={12}
                  value={mesFim}
                  onChange={(e) => setMesFim(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: 13,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </Row>

            {/* Banner de Temporalidade Contábil */}
            <div style={{ background: '#eff6ff', padding: 12, borderRadius: 6, border: '1px solid #bfdbfe', marginBottom: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                ℹ️ Regra de Temporalidade Contábil (Restos a Pagar)
              </span>
              <p style={{ fontSize: 12, color: '#1e3a8a', margin: 0, lineHeight: 1.4 }}>
                O <strong>Exercício ({exercicio})</strong> determina o período dos <strong>Doc-Caixa</strong> e das <strong>Licitações</strong>. 
                Para os <strong>Empenhos</strong>, o sistema analisa automaticamente os pagamentos e busca os empenhos nos respectivos anos de emissão no TCE-CE (garantindo a localização de Restos a Pagar).
              </p>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                Anos Adicionais de Empenhos / Restos a Pagar (Opcional)
              </label>
              <input
                type='text'
                placeholder='Ex: 2023, 2022 (ou deixe vazio para detecção automática)'
                value={anosEmpenhosTexto}
                onChange={(e) => setAnosEmpenhosTexto(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: 13,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ background: '#f8fafc', padding: '14px 16px', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 18 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: 10 }}>
                Coleções a Ingerir:
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: '10px 12px',
                  }}
                >
                  <input
                    type='checkbox'
                    checked={syncDespesas}
                    onChange={(e) => setSyncDespesas(e.target.checked)}
                    style={{ marginTop: 2, cursor: 'pointer', accentColor: '#2563eb' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                      Despesas (Doc-Caixa, Empenhos e Extraorçamentárias)
                    </span>
                    <span style={{ fontSize: 11.5, color: '#475569' }}>
                      Sincroniza notas de pagamento do exercício e empenhos correspondentes no TCE-CE
                    </span>
                  </div>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: '10px 12px',
                  }}
                >
                  <input
                    type='checkbox'
                    checked={syncLicitacoes}
                    onChange={(e) => setSyncLicitacoes(e.target.checked)}
                    style={{ marginTop: 2, cursor: 'pointer', accentColor: '#2563eb' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                      Processos Licitatórios e Fornecedores
                    </span>
                    <span style={{ fontSize: 11.5, color: '#475569' }}>
                      Sincroniza licitações, contratos e dados dos fornecedores homologados
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <Button
              label={syncTceMutation.isLoading ? 'Sincronizando com TCE-CE...' : 'Sincronizar via API TCE-CE'}
              type='submit'
              color='primary'
              variantType='block'
              loading={syncTceMutation.isLoading}
            />
          </form>
        )}

        {/* Tab 2: Standard URL Ingestion */}
        {activeTab === 'standard' && (
          <Form onSubmit={handleSubmit(onSubmitStandard)}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 15 }}>
              Informe o período no formato YYYYMM para sincronizar via URL genérica configurada.
            </p>

            <Row justify='space-between' gap={10}>
              <div style={{ flex: 1 }}>
                <Input
                  type='text'
                  control={control}
                  name='startDate'
                  label='Período Inicial'
                  placeholder='Ex: 202401'
                  error={errors.startDate}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Input
                  type='text'
                  control={control}
                  name='endDate'
                  label='Período Final'
                  placeholder='Ex: 202412'
                  error={errors.endDate}
                />
              </div>
            </Row>

            <div style={{ marginTop: 20 }}>
              <Button
                label='Iniciar Sincronização Padrão'
                type='submit'
                color='primary'
                variantType='block'
                loading={syncMutation.isLoading}
              />
            </div>
          </Form>
        )}
      </Container>
    </Modal>
  );
};

export default React.forwardRef(SyncModal);
